// /admin/lab — the Sound Lab: the full song editor on any sound setup (any kit, key, and any
// preset as main or alternate of each instrument), to test kits and instruments. Load a day's
// setup or one from the Planner, generate a Wizard demo song, browse and audition every kit and
// preset, export/import songs as JSON. Nothing is submitted or stored on the server.
import { useEffect, useRef, useState } from "react";
import {
  DELAY_TICKS,
  getPreset,
  KIT_IDS,
  KITS,
  makePeriodDef,
  NOTE_NAMES,
  periodSpec,
  ROLE_SLOT,
  sanitizeLoop,
  scaleNotesInRange,
  slotRange,
  type InstrumentRole,
  type KitId,
  type LoopData,
  type PeriodDef,
} from "@eb/shared";
import { api, ApiError } from "../../api";
import { fetchKit } from "../../audio/host";
import { pushNow, stopPlay } from "../../editor/audio";
import { useEditorKeyboard } from "../../editor/keyboard";
import { MixPanel } from "../../editor/MixPanel";
import { previewSound } from "../../editor/padInput";
import { Pads } from "../../editor/Pads";
import { SongPanel } from "../../editor/SongPanel";
import { SoundPicker } from "../../editor/SoundPicker";
import { loadIntoEditor, useEditor } from "../../editor/store";
import { TrackGrid } from "../../editor/TrackGrid";
import { Transport } from "../../editor/Transport";
import { useSearch, useTitle } from "../../lib/router";
import { stopRiverIfPlaying } from "../../river/context";
import { AdminShell } from "./AdminShell";
import { DELAY_LABEL, setupOf } from "./Planner";
import { PresetSelect } from "./PresetSelect";
import { decodeSetup, type LabSetup } from "./labSetup";
import { defaultAlternates, familiesOf, INSTRUMENT_ROLES, KIT_OPTIONS, ROLE_LABEL } from "./setup";

/** The Lab's period: a day far beyond any real one. */
const LAB_DAY = 1_000_000;

const DEFAULT_SETUP: LabSetup = {
  kit: "techno",
  key: { tonic: 9, mode: "minor" },
  instruments: {
    bass: "rumble-sub",
    harmony: "dub-chord",
    lead: "hoover-lead",
    classic: "violin-section",
  },
  alternates: {
    bass: defaultAlternates("rumble-sub"),
    harmony: defaultAlternates("dub-chord"),
    lead: defaultAlternates("hoover-lead"),
    classic: defaultAlternates("violin-section"),
  },
  fx: { reverbSize: 0.55, delayTicks: 720 },
  seed: 0x1ab,
};

function defOf(s: LabSetup): PeriodDef {
  return makePeriodDef({
    day: LAB_DAY,
    key: s.key,
    kit: s.kit,
    instruments: s.instruments,
    alternates: s.alternates,
    fx: s.fx,
    seed: s.seed,
  });
}

export function SoundLab() {
  useTitle("Sound Lab");
  // The query string (a stable string: useSearch() returns a new object every render).
  const qs = useSearch().toString();
  const [setup, setSetup] = useState<LabSetup | null>(null);
  const [source, setSource] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const variation = useRef(0);
  const ready = useEditor((s) => s.def?.day === LAB_DAY);
  useEditorKeyboard();

  // The River shares the engine: stop it while testing sounds.
  useEffect(() => {
    void stopRiverIfPlaying();
    return () => void stopPlay();
  }, []);

  // Start from ?setup= (Planner), ?day= (a real day's sounds) or the default.
  useEffect(() => {
    const search = new URLSearchParams(qs);
    const enc = search.get("setup");
    const day = search.get("day");
    if (enc) {
      const s = decodeSetup(enc);
      setSetup(s ?? DEFAULT_SETUP);
      setSource(s ? "Setup from the Planner (not saved)" : "Default setup");
    } else if (day) {
      api<PeriodDef>("GET", `/api/periods/${encodeURIComponent(day)}`)
        .then((def) => {
          setSetup(setupOf(def));
          setSource(`Sounds of day ${def.day}`);
        })
        .catch((e) => {
          setMsg(e instanceof ApiError ? e.message : String(e));
          setSetup(DEFAULT_SETUP);
        });
    } else {
      setSetup(DEFAULT_SETUP);
      setSource("Default setup");
    }
  }, [qs]);

  // Apply the setup to the editor (keeping the song; a new key snaps its notes into the key).
  useEffect(() => {
    if (!setup) return;
    let alive = true;
    void (async () => {
      const def = defOf(setup);
      const { manifest } = await fetchKit(def.kit);
      if (!alive) return;
      const spec = periodSpec(def, manifest);
      const st = useEditor.getState();
      if (st.def?.day === LAB_DAY && st.def.key.tonic === def.key.tonic && st.def.key.mode === def.key.mode) {
        useEditor.setState({ def, spec });
      } else {
        const song = st.def?.day === LAB_DAY ? st.hist.present : null;
        loadIntoEditor("lab", def, spec, song ? sanitizeLoop(song, def.key, []) : null, "Lab song");
      }
    })();
    return () => {
      alive = false;
    };
  }, [setup]);

  if (!setup)
    return (
      <AdminShell wide>
        <p className="muted">Loading…</p>
      </AdminShell>
    );

  const update = (p: Partial<LabSetup>) => setSetup({ ...setup, ...p });
  const setMain = (r: InstrumentRole, id: string) =>
    update({
      instruments: { ...setup.instruments, [r]: id },
      alternates: { ...setup.alternates, [r]: defaultAlternates(id, setup.seed) },
    });

  const generate = async () => {
    setBusy(true);
    setMsg("");
    try {
      variation.current += 1;
      const r = await api<{ loop: LoopData }>("POST", "/api/admin/lab/song", {
        ...setup,
        variation: variation.current,
      });
      const st = useEditor.getState();
      if (st.def && st.spec) loadIntoEditor("lab", st.def, st.spec, r.loop, `Demo ${variation.current}`);
      setMsg(`Wizard demo song ${variation.current} loaded — press “Play song”.`);
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const exportSong = () => {
    const st = useEditor.getState();
    const blob = new Blob([JSON.stringify({ setup, song: st.hist.present }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `sound-lab-${setup.kit}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importSong = async (file: File) => {
    try {
      const j = JSON.parse(await file.text()) as { setup?: LabSetup; song?: LoopData };
      if (!j.song || !Array.isArray(j.song.events)) throw new Error("That file has no song.");
      const next = j.setup ?? setup;
      const def = defOf(next);
      const { manifest } = await fetchKit(def.kit);
      loadIntoEditor("lab", def, periodSpec(def, manifest), sanitizeLoop(j.song, def.key, []), "Imported");
      setSetup(next);
      setMsg(`Imported ${file.name}.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const auditionPreset = (r: InstrumentRole, id: string) => {
    setMain(r, id);
    // After the new sound reached the engine: a short phrase in the key.
    setTimeout(() => {
      pushNow();
      const slot = ROLE_SLOT[r];
      const range = slotRange(slot)!;
      const notes = scaleNotesInRange(setup.key, range.lo, range.hi);
      const mid = Math.floor(notes.length / 3);
      [0, 2, 4].forEach((k, i) => setTimeout(() => previewSound(slot, notes[mid + k] ?? notes[0], 100), i * 300));
    }, 250);
  };

  const auditionKit = (kit: KitId) => {
    update({ kit });
    setTimeout(() => {
      pushNow();
      for (let pad = 0; pad < 16; pad++) setTimeout(() => previewSound(pad, 60, 110), pad * 260);
    }, 400);
  };

  return (
    <AdminShell wide>
      <h1>Sound Lab</h1>
      <p className="muted">
        The full editor on any sounds — {source}. Change the kit, key or any instrument and keep editing; nothing is
        saved on the server.
      </p>

      <section className="panel lab-setup" aria-label="Sound setup" data-testid="lab-setup">
        <div className="admin-grid">
          <label className="stack-label">
            <span>Drum kit (genre)</span>
            <select value={setup.kit} onChange={(e) => update({ kit: e.target.value as KitId })} data-testid="lab-kit">
              {KIT_OPTIONS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
          <label className="stack-label">
            <span>Key</span>
            <span className="row">
              <select
                value={setup.key.tonic}
                onChange={(e) => update({ key: { ...setup.key, tonic: Number(e.target.value) } })}
                aria-label="Tonic"
              >
                {NOTE_NAMES.map((n, i) => (
                  <option key={n} value={i}>
                    {n}
                  </option>
                ))}
              </select>
              <select
                value={setup.key.mode}
                onChange={(e) => update({ key: { ...setup.key, mode: e.target.value as "major" | "minor" } })}
                aria-label="Mode"
              >
                <option value="major">major</option>
                <option value="minor">minor</option>
              </select>
            </span>
          </label>
          <label className="stack-label">
            <span>Reverb {Math.round(setup.fx.reverbSize * 100)}%</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={setup.fx.reverbSize}
              onChange={(e) => update({ fx: { ...setup.fx, reverbSize: Number(e.target.value) } })}
            />
          </label>
          <label className="stack-label">
            <span>Delay</span>
            <select
              value={setup.fx.delayTicks}
              onChange={(e) => update({ fx: { ...setup.fx, delayTicks: Number(e.target.value) } })}
            >
              {DELAY_TICKS.map((d) => (
                <option key={d} value={d}>
                  {DELAY_LABEL[d]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="lab-roles">
          {INSTRUMENT_ROLES.map((r) => (
            <fieldset key={r} className="role-fieldset">
              <legend>{ROLE_LABEL[r]}</legend>
              <label className="stack-label">
                <span>Main</span>
                <PresetSelect
                  role={r}
                  value={setup.instruments[r]}
                  onChange={(id) => setMain(r, id)}
                  label={`${ROLE_LABEL[r]} main`}
                  testId={`lab-${r}`}
                />
              </label>
              {[0, 1].map((i) => (
                <label key={i} className="stack-label">
                  <span>Alternate {i + 1}</span>
                  <PresetSelect
                    role={r}
                    value={setup.alternates[r][i]}
                    onChange={(id) => {
                      const pair = [...setup.alternates[r]] as [string, string];
                      pair[i] = id;
                      update({ alternates: { ...setup.alternates, [r]: pair } });
                    }}
                    label={`${ROLE_LABEL[r]} alternate ${i + 1}`}
                  />
                </label>
              ))}
            </fieldset>
          ))}
        </div>
        <div className="row lab-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void generate()}
            disabled={busy || !ready}
            data-testid="lab-generate"
          >
            {busy ? "Composing…" : "✨ Generate demo song"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const st = useEditor.getState();
              if (st.def && st.spec) loadIntoEditor("lab", st.def, st.spec, null, "Lab song");
            }}
            disabled={!ready}
          >
            New empty song
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => update({ seed: (setup.seed * 1103515245 + 12345) >>> 0 })}
          >
            New drum breaks
          </button>
          <button type="button" className="btn" onClick={exportSong} disabled={!ready}>
            Export JSON
          </button>
          <label className="btn">
            Import JSON
            <input
              type="file"
              accept="application/json,.json"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void importSong(f);
                e.target.value = "";
              }}
            />
          </label>
          {msg && (
            <span role="status" className="small">
              {msg}
            </span>
          )}
        </div>
      </section>

      {ready ? (
        <>
          <Transport />
          <SongPanel />
          <div className="editor-main">
            <TrackGrid />
            <aside className="editor-side">
              <Pads />
              <MixPanel />
              <SoundPicker />
            </aside>
          </div>
        </>
      ) : (
        <p className="muted">Loading sounds…</p>
      )}

      <SoundLibrary setup={setup} onKit={auditionKit} onPreset={auditionPreset} />
    </AdminShell>
  );
}

/** Every kit and preset, with one-click auditions. */
function SoundLibrary({
  setup,
  onKit,
  onPreset,
}: {
  setup: LabSetup;
  onKit: (k: KitId) => void;
  onPreset: (r: InstrumentRole, id: string) => void;
}) {
  const [manifests, setManifests] = useState<Record<string, string[]>>({});
  useEffect(() => {
    void Promise.all(
      KIT_IDS.map((k) => fetchKit(k).then(({ manifest }) => [k, manifest.pads.map((p) => p.name)] as const)),
    ).then((all) => setManifests(Object.fromEntries(all)));
  }, []);
  return (
    <section className="panel stack lab-library" aria-labelledby="lib-h" data-testid="lab-library">
      <h2 id="lib-h">Sound library</h2>
      <h3>Drum kits ({KIT_IDS.length})</h3>
      <div className="lab-kits">
        {KIT_IDS.map((k) => (
          <article key={k} className={`lab-kit${setup.kit === k ? " on" : ""}`}>
            <header>
              <strong>{KITS[k].name}</strong>
              <span className="chip">{KITS[k].genre}</span>
            </header>
            <p className="muted small">{manifests[k]?.join(" · ") ?? "…"}</p>
            <button type="button" className="btn btn-small" onClick={() => onKit(k)} data-testid={`lab-kit-${k}`}>
              ▶ Use &amp; play all pads
            </button>
          </article>
        ))}
      </div>
      {INSTRUMENT_ROLES.map((r) => (
        <div key={r}>
          <h3>
            {ROLE_LABEL[r]} presets ({familiesOf(r).reduce((n, f) => n + f.presets.length, 0)})
          </h3>
          <div className="lab-families">
            {familiesOf(r).map(({ family, presets }) => (
              <div key={family} className="lab-family">
                <span className="lab-family-name">{family}</span>
                <div className="row">
                  {presets.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`chip-btn${setup.instruments[r] === p.id ? " on" : ""}`}
                      title={`${p.flavour} · ${p.params.model}${p.alternateOnly ? "" : " · can be a day's main"}`}
                      onClick={() => onPreset(r, p.id)}
                      data-testid={`lab-preset-${p.id}`}
                    >
                      {p.name}
                      {p.alternateOnly ? "" : " ★"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <p className="muted small">
        Clicking a preset makes it the {"instrument's"} main sound here and plays a short phrase; clicking a kit loads
        it and plays its 16 pads. ★ = can be a day&apos;s main sound. Current: {getPreset(setup.instruments.bass).name},{" "}
        {getPreset(setup.instruments.harmony).name}, {getPreset(setup.instruments.lead).name}.
      </p>
    </section>
  );
}
